// src/functions/clampValue.ts
function clampValue(value, min, max) {
  return value <= min ? min : value >= max ? max : value;
}

// src/functions/easings.ts
function linear(t) {
  return t;
}
function easeInQuad(t) {
  return t * t;
}
function easeOutQuad(t) {
  return t * (2 - t);
}
function easeInOutQuad(t) {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
}
function easeOutBack(t, o = 1.25) {
  return 1 + (t - 1) * (t - 1) * ((o + 1) * (t - 1) + o);
}
function easeInCubic(t) {
  return t * t * t;
}
function easeOutCubic(t) {
  return --t * t * t + 1;
}
function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
}
function easeInQuart(t) {
  return t * t * t * t;
}
function easeOutQuart(t) {
  return 1 - --t * t * t * t;
}
function easeInOutQuart(t) {
  return t < 0.5 ? 8 * t * t * t * t : 1 - 8 * --t * t * t * t;
}
function easeInQuint(t) {
  return t * t * t * t * t;
}
function easeOutQuint(t) {
  return 1 + --t * t * t * t * t;
}
function easeInOutQuint(t) {
  return t < 0.5 ? 16 * t * t * t * t * t : 1 + 16 * --t * t * t * t * t;
}

// src/functions/toNumber.ts
function toNumber(value) {
  return typeof value === "string" ? parseFloat(value) : Number(value);
}

// src/functions/formatTime.ts
function formatTime(seconds, guide) {
  seconds = seconds < 0 ? 0 : seconds;
  let s = Math.floor(seconds % 60);
  let m = Math.floor(seconds / 60 % 60);
  let h = Math.floor(seconds / 3600);
  const gm = Math.floor(guide / 60 % 60);
  const gh = Math.floor(guide / 3600);
  if (isNaN(seconds) || seconds === Infinity) {
    h = m = s = "-";
  }
  h = toNumber(h) > 0 || gh > 0 ? h + ":" : "";
  m = ((h || gm >= 10) && toNumber(m) < 10 ? "0" + m : m) + ":";
  s = toNumber(s) < 10 ? "0" + s : s;
  return h + m + s;
}

// src/functions/interpolate.ts
function interpolate(args) {
  const { from, to, duration, callback, easing = (t) => t * (2 - t) } = args;
  let startTime;
  function animate(timestamp) {
    if (!startTime) startTime = timestamp;
    const progress = Math.min(1, (timestamp - startTime) / duration);
    const easedProgress = easing(progress);
    const value = from + (to - from) * easedProgress;
    callback(value);
    if (progress < 1) {
      requestAnimationFrame(animate);
    }
  }
  requestAnimationFrame(animate);
}

// src/functions/isIOS.ts
function isIOS() {
  if (typeof window === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator == null ? void 0 : navigator.userAgent);
}

// src/functions/isWithinRange.ts
function isWithinRange(args) {
  const { input, base, threshold } = args;
  return input >= base - threshold && input <= base + threshold;
}

// src/functions/mapValue.ts
function mapValue(value, inMin, inMax, outMin, outMax) {
  return (value - inMin) * (outMax - outMin) / (inMax - inMin) + outMin;
}

// src/functions/matchClass.ts
function matchClass(el, regex) {
  return Array.from(el.classList).some((className) => regex.test(className));
}

// src/functions/pointerCapture.ts
function guardedSetPointerCapture(args) {
  const { event, element, debug } = args;
  if (element && event.isPrimary && event.pointerType !== "mouse") {
    try {
      const coalescedEvents = "getCoalescedEvents" in event ? event.getCoalescedEvents() : [event];
      if (coalescedEvents.length > 0) {
        element == null ? void 0 : element.setPointerCapture(event.pointerId);
      }
    } catch (error) {
      if (debug) {
        console.warn("Failed to set pointer capture:", error);
      }
    }
  }
}
function guardedReleasePointerCapture(args) {
  const { event, element } = args;
  if (element == null ? void 0 : element.hasPointerCapture(event.pointerId)) {
    element.releasePointerCapture(event.pointerId);
  }
}

// src/functions/slugify.ts
var defaultOptions = {
  separator: "-",
  trim: true,
  remove: void 0,
  strict: true,
  lowercase: true
};
function slugify(string, options) {
  if (typeof string !== "string") {
    throw new Error("slugify: string argument expected");
  }
  const _options = { ...defaultOptions, ...options };
  const charMap = {};
  let slug = string.normalize().split("").reduce(function(result, ch) {
    let appendChar = charMap[ch];
    if (appendChar === void 0) appendChar = ch;
    if (appendChar === (_options == null ? void 0 : _options.separator)) appendChar = " ";
    return result + appendChar.replace((_options == null ? void 0 : _options.remove) || /[^\w\s$*_+~.()'"!\-:@]+/g, "");
  }, "");
  if (_options.strict) {
    slug = slug.replace(/[^A-Za-z0-9\s]/g, "");
  }
  if (_options.trim) {
    slug = slug.trim();
  }
  if (_options.separator) {
    slug = slug.replace(/ +/g, _options.separator);
  }
  if (_options.lowercase) {
    slug = slug.toLocaleLowerCase();
  }
  return slug;
}

// src/functions/uniq.ts
function uniq(a) {
  return Array.from(new Set(a));
}
export {
  clampValue,
  easeInCubic,
  easeInOutCubic,
  easeInOutQuad,
  easeInOutQuart,
  easeInOutQuint,
  easeInQuad,
  easeInQuart,
  easeInQuint,
  easeOutBack,
  easeOutCubic,
  easeOutQuad,
  easeOutQuart,
  easeOutQuint,
  formatTime,
  guardedReleasePointerCapture,
  guardedSetPointerCapture,
  interpolate,
  isIOS,
  isWithinRange,
  linear,
  mapValue,
  matchClass,
  slugify,
  uniq
};
//# sourceMappingURL=index.js.map